import { Texto, Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { Button, Form } from 'antd'
import type { FormInstance } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import type { Ratebook } from '@api/useFreight/types'
import { useActivationForm } from '../hooks'
import { daysOfWeek } from '../constants'

interface ActivationFormProps {
  form: FormInstance
  ratebook: Ratebook
  setIsPopoverOpen: (isOpen: boolean) => void
  setCurrentStatus: (status: string) => void
}

export function ActivationForm({ form, ratebook, setIsPopoverOpen, setCurrentStatus }: ActivationFormProps) {
  const {
    selectedFrequency,
    selectedDay,
    nextInvoiceDate,
    endOfMonth,
    endOfDay,
    handleSubmit,
    createRatebookMutation,
    contextHolder,
  } = useActivationForm(ratebook, form, setIsPopoverOpen, setCurrentStatus)

  return (
    <>
      {contextHolder}
      <Form
        form={form}
        initialValues={{
          billing_frequency: selectedFrequency,
          dayOfWeek: selectedFrequency === 'Weekly' ? daysOfWeek.indexOf(selectedDay) : undefined,
        }}
        onFinish={handleSubmit}
        layout='vertical'
        style={{ minWidth: 300 }}
      >
        <Vertical gap={8}>
          <Horizontal verticalCenter justifyContent='space-between' className='py-3' gap={16}>
            <Texto style={{ width: 120 }} weight='bold'>
              Billing Frequency:
            </Texto>
            <Texto weight='bold' category='p2'>
              {selectedFrequency}
            </Texto>
          </Horizontal>

          {selectedFrequency === 'Monthly' && (
            <>
              <Texto>
                All orders completed from now until {endOfMonth.format('MM/DD/YYYY')} will be included in the first
                invoice.
              </Texto>
              <Texto appearance='hint'>
                Future invoices will be generated on the 1st of each month, capturing all orders from the previous
                month.
              </Texto>
            </>
          )}

          {selectedFrequency === 'Weekly' && (
            <>
              <Horizontal justifyContent='space-between' verticalCenter gap={16}>
                <Texto style={{ width: 120 }}>Day of Week:</Texto>
                <Texto weight='bold' category='p2'>
                  {selectedDay}
                </Texto>
              </Horizontal>
              {nextInvoiceDate && (
                <>
                  <Texto>
                    All orders completed from now until {dayjs(nextInvoiceDate).format('MM/DD/YYYY')} will be included
                    in the first invoice.
                  </Texto>
                  <Texto appearance='hint'>
                    Future invoices will be generated weekly on {selectedDay}s, capturing all orders from the previous
                    week.
                  </Texto>
                </>
              )}
            </>
          )}

          {selectedFrequency === 'Daily' && (
            <>
              <Texto>
                All orders completed from now until {endOfDay.format('MM/DD h:mmA')}, EOD will be included in today's
                invoice.
              </Texto>
              <Texto appearance='hint'>
                Future invoices will be generated daily, capturing all orders from the previous day.
              </Texto>
            </>
          )}

          <Horizontal gap={8} style={{ marginTop: 16 }}>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button onClick={() => setIsPopoverOpen(false)}>Cancel</Button>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, flex: 1 }}>
              <Button type='primary' htmlType='submit' block loading={createRatebookMutation.isPending}>
                Activate Invoicing
              </Button>
            </Form.Item>
          </Horizontal>
        </Vertical>
      </Form>
    </>
  )
}
